import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Building2, MapPin, Phone, Save, Loader2,
  Link2, FileText, CheckCircle, AlertCircle, Search, ArrowRight, Plus, X,
  Palette, Image as ImageIcon, Tag, Eye, Megaphone, Code, Gift, ExternalLink,
  Sparkles, Film, User, Award, Zap, Share2, GripVertical, ChevronLeft, ChevronRight, Trash2
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, arrayMove, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import HorsepowerDashboard from "@/components/dashboard/HorsepowerDashboard";
import WebsiteBadgeTab from "@/components/dashboard/WebsiteBadgeTab";
import SocialPreviewTab from "@/components/dashboard/SocialPreviewTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import FileUpload from "@/components/dashboard/FileUpload";
import ShortRecorder from "@/components/dashboard/ShortRecorder";
import MediaUrlAdder from "@/components/dashboard/MediaUrlAdder";
import { normalizeFacebookUrl as normalizeFbUrl } from "@/components/FacebookPageEmbed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const tabs = [
  { id: "horsepower", label: "Horsepower", icon: Zap },
  { id: "branding", label: "Branding", icon: Palette },
  { id: "about", label: "About Us", icon: FileText },
  { id: "offers", label: "Offers", icon: Gift },
  { id: "gallery", label: "Gallery", icon: ImageIcon },
  { id: "shorts", label: "Shorts", icon: Film },
  { id: "social", label: "Social & Embeds", icon: Link2 },
  { id: "contact", label: "Contact & Hours", icon: Phone },
  { id: "location", label: "Location", icon: MapPin },
  { id: "cta", label: "Call to Action", icon: Megaphone },
  { id: "badge", label: "Website Badge", icon: Award },
  { id: "social-preview", label: "Social Preview", icon: Share2 },
];

export default function ListingManagement() {
  const { user, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState("branding");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listing, setListing] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [mode, setMode] = useState(null);

  // Drag-and-drop sensors — pointer with a tiny activation distance so simple
  // clicks on the video/buttons inside the card don't accidentally start a drag.
  const shortsSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [claimSearch, setClaimSearch] = useState("");
  const [claimResults, setClaimResults] = useState([]);
  const [claimSearching, setClaimSearching] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [autoImporting, setAutoImporting] = useState(false);
  const [autoImportResult, setAutoImportResult] = useState(null);
  const [brandUpdateNotice, setBrandUpdateNotice] = useState(null);
  const [checkingBrand, setCheckingBrand] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [newListing, setNewListing] = useState({
    name: "", category_name: "", description: "", street_address: "",
    city: "", state: "", zip_code: "", phone: "", email: "", website: "",
    is_individual: null, // null = not yet chosen; user must pick Business or Pro/Agent
  });

  useEffect(() => { fetchListing(); }, []);

  // Brand-update check: pings Brandfetch (cheap, no LLM/scrape) once per 7 days per listing.
  // Shows a banner if the business's website branding has changed since last import.
  useEffect(() => {
    if (!listing?.id || !listing?.website || !listing?.brand_primary_color) return;
    const cacheKey = `lb_brandcheck_${listing.id}`;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || "null");
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (cached && Date.now() - cached.t < sevenDays) {
        if (cached.has_updates) setBrandUpdateNotice(cached);
        return;
      }
    } catch (e) { console.warn("brandcheck cache read failed:", e); }
    (async () => {
      setCheckingBrand(true);
      try {
        const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.post(`${API_ROOT}/listings/brand-check`, { business_id: listing.id });
        const data = { ...res.data, t: Date.now() };
        try { localStorage.setItem(cacheKey, JSON.stringify(data)); } catch (e) { console.warn("brandcheck cache write failed:", e); }
        if (data.has_updates) setBrandUpdateNotice(data);
      } catch (e) {
        console.warn("brand-check API call failed (non-critical):", e?.message);
      } finally {
        setCheckingBrand(false);
      }
    })();
  }, [listing?.id, listing?.website, listing?.brand_primary_color]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API}/listings/my`, { headers: getAuthHeader() });
      if (res.data) {
        // Merge auto-imported `images[]` (string URLs from Brandfetch/website scraper)
        // into the editable `gallery[]` so the owner can preview & delete them right
        // alongside their manually-uploaded media. On save we clear `images: []`
        // because all media now lives in gallery.
        const importedStrings = Array.isArray(res.data.images) ? res.data.images.filter(Boolean) : [];
        const existingGallery = Array.isArray(res.data.gallery) ? res.data.gallery : [];
        const existingUrls = new Set(
          existingGallery.map((g) => (typeof g === "string" ? g : g?.url)).filter(Boolean)
        );
        const importedItems = importedStrings
          .filter((u) => !existingUrls.has(u))
          .map((url) => ({
            id: `imported-${Math.random().toString(36).slice(2, 9)}`,
            url,
            type: "image",
            source: "auto_import",
            caption: "",
          }));
        const mergedGallery = [...existingGallery, ...importedItems];

        setListing({
          ...res.data,
          offers: res.data.offers || [],
          gallery: mergedGallery,
          // Mark imported array as empty in local state so save clears it server-side.
          images: importedItems.length ? [] : (res.data.images || []),
          shorts: res.data.shorts || [],
          social_links: res.data.social_links || {},
          custom_embeds: res.data.custom_embeds || [],
          hours: res.data.hours || {},
        });
        if (importedItems.length) {
          // Visually mark unsaved so the owner knows to hit Save after curating.
          setHasChanges(true);
        }
      }
    } catch (err) {
      console.error("Failed to fetch listing:", err);
    } finally {
      setLoading(false);
    }
  };

  const set = (field, value) => {
    setListing(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const setNested = (parent, field, value) => {
    setListing(prev => ({ ...prev, [parent]: { ...(prev[parent] || {}), [field]: value } }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/listings/${listing.id || listing.slug}`, listing, { headers: getAuthHeader() });
      // Backend may return an updated record with a new slug (URL rename). Sync local state.
      if (res?.data) {
        setListing((prev) => ({ ...prev, ...res.data }));
      }
      toast.success("Listing saved!");
      setHasChanges(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // --- Auto Import from Website (Brandfetch + LLM + scraper) ---
  const runAutoImport = async (opts = {}) => {
    if (!listing?.website) {
      toast.error("Add a website URL first");
      return;
    }
    setAutoImporting(true);
    setAutoImportResult(null);
    try {
      const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const res = await axios.post(`${API_ROOT}/listings/auto-import`, {
        website_url: listing.website,
        business_id: listing.id,
        apply_to_listing: true,
        force: !!opts.force,
      });
      setAutoImportResult(res.data);
      // Reflect applied fields locally so user sees them immediately
      const brand = res.data?.brand || {};
      const scraped = res.data?.scraped || {};
      const updates = {};
      if (brand.logo_url && (opts.force || !listing.logo_url)) updates.logo_url = brand.logo_url;
      if (brand.brand_primary_color) updates.brand_primary_color = brand.brand_primary_color;
      if (brand.brand_secondary_color) updates.brand_secondary_color = brand.brand_secondary_color;
      if (res.data?.about_us_html && (opts.force || !(listing.about_us || "").trim())) updates.about_us = res.data.about_us_html;
      const newImgs = scraped.images || [];
      if (newImgs.length) {
        const existing = listing.images || [];
        updates.images = Array.from(new Set([...existing, ...newImgs])).slice(0, 12);
      }
      if (Object.keys(updates).length) {
        setListing((prev) => ({ ...prev, ...updates }));
      }
      toast.success(`Auto-import complete — ${(res.data?.applied_fields || []).length} field(s) updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto-import failed");
    } finally {
      setAutoImporting(false);
    }
  };

  // --- Claim ---
  const searchBusinesses = async () => {
    if (claimSearch.length < 2) return;
    setClaimSearching(true);
    try {
      const res = await axios.get(`${API}/listings/search?q=${encodeURIComponent(claimSearch)}`, { headers: getAuthHeader() });
      setClaimResults(res.data?.results || []);
    } catch { toast.error("Search failed"); }
    finally { setClaimSearching(false); }
  };

  const handleClaim = async (slug) => {
    setClaiming(true);
    try {
      await axios.post(`${API}/listings/claim`, { slug }, { headers: getAuthHeader() });
      toast.success("Claim submitted! An admin will review your request.");
      setMode(null);
      await fetchListing();
    } catch (err) { toast.error(err.response?.data?.detail || "Claim failed"); }
    finally { setClaiming(false); }
  };

  // --- Create ---
  const handleCreate = async () => {
    if (!newListing.name.trim()) { toast.error("Business name is required"); return; }
    if (newListing.is_individual === null) { toast.error("Please choose Business or Pro / Agent first"); return; }
    setCreating(true);
    try {
      const payload = { ...newListing, is_individual: !!newListing.is_individual };
      const createRes = await axios.post(`${API}/listings/create`, payload, { headers: getAuthHeader() });
      toast.success("Listing created!");
      // If the user supplied a website, auto-import branding in the background
      const createdId = createRes.data?.id || createRes.data?.business?.id || createRes.data?.listing?.id;
      if (newListing.website && createdId) {
        try {
          const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;
          toast.message("Auto-importing branding from your website…");
          await axios.post(`${API_ROOT}/listings/auto-import`, {
            website_url: newListing.website,
            business_id: createdId,
            apply_to_listing: true,
          });
          toast.success("Branding auto-imported from your website");
        } catch (e) {
          // non-fatal — listing still created
          console.warn("Auto-import on create failed", e);
        }
      }
      setMode(null);
      await fetchListing();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  // --- Delete My Listing (start over) ---
  const handleDeleteListing = async () => {
    if (deleteConfirmText.trim().toUpperCase() !== "DELETE") {
      toast.error('Type DELETE to confirm');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/listings/my`, { headers: getAuthHeader() });
      toast.success("Listing deleted. You can now create a fresh one.");
      setShowDeleteConfirm(false);
      setDeleteConfirmText("");
      // Reset local state so the create/claim chooser shows
      setListing(null);
      setHasChanges(false);
      setMode(null);
      await fetchListing();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete listing");
    } finally {
      setDeleting(false);
    }
  };

  // --- Offers helpers ---
  const addOffer = () => {
    const offers = [...(listing.offers || []), { id: Date.now().toString(), title: "", description: "", image_url: "", video_url: "" }];
    set("offers", offers);
  };

  const updateOffer = (idx, field, value) => {
    const offers = [...(listing.offers || [])];
    offers[idx] = { ...offers[idx], [field]: value };
    set("offers", offers);
  };

  const removeOffer = (idx) => {
    set("offers", (listing.offers || []).filter((_, i) => i !== idx));
  };

  // --- Gallery helpers ---
  const addGalleryItem = (url, data) => {
    if (!url) return;
    const gallery = [...(listing.gallery || []), {
      id: data?.file_id || Date.now().toString(),
      url,
      caption: data?.filename || "",
      type: data?.content_type?.startsWith("video") ? "video" : "image",
    }];
    set("gallery", gallery);
  };

  const removeGalleryItem = (idx) => {
    set("gallery", (listing.gallery || []).filter((_, i) => i !== idx));
  };

  // --- Custom embeds ---
  const addEmbed = () => {
    const embeds = [...(listing.custom_embeds || []), { id: Date.now().toString(), title: "", code: "" }];
    set("custom_embeds", embeds);
  };

  const updateEmbed = (idx, field, value) => {
    const embeds = [...(listing.custom_embeds || [])];
    embeds[idx] = { ...embeds[idx], [field]: value };
    set("custom_embeds", embeds);
  };

  const removeEmbed = (idx) => {
    set("custom_embeds", (listing.custom_embeds || []).filter((_, i) => i !== idx));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // --- No Listing State ---
  if (!listing) {
    return (
      <div className="space-y-6" data-testid="listing-management">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">My Listing</h1>
          <p className="text-[#94A3B8] mt-1">Manage your business listing</p>
        </div>
        <AnimatePresence mode="wait">
          {!mode && (
            <motion.div key="choose" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="bg-[#0F172A] border border-white/10 rounded-xl p-8">
              <div className="text-center mb-8">
                <Building2 className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-white mb-2">Get Started with Your Listing</h2>
                <p className="text-[#94A3B8] max-w-lg mx-auto">Already in the directory? Claim your business. Starting fresh? Create a new listing.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <button onClick={() => setMode("claim")} className="p-6 bg-white/5 border border-white/10 rounded-xl hover:border-[#7C3AED]/50 hover:bg-[#7C3AED]/5 transition-all text-left group" data-testid="claim-listing-btn">
                  <Search className="w-8 h-8 text-[#7C3AED] mb-3" />
                  <h3 className="text-white font-semibold mb-1">Claim Existing Listing</h3>
                  <p className="text-sm text-[#94A3B8]">Search for your business in our directory and claim it</p>
                  <ArrowRight className="w-4 h-4 text-[#7C3AED] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <button onClick={() => setMode("create")} className="p-6 bg-white/5 border border-white/10 rounded-xl hover:border-[#10B981]/50 hover:bg-[#10B981]/5 transition-all text-left group" data-testid="create-listing-btn">
                  <Plus className="w-8 h-8 text-[#10B981] mb-3" />
                  <h3 className="text-white font-semibold mb-1">Create New Listing</h3>
                  <p className="text-sm text-[#94A3B8]">Add your business to the directory from scratch</p>
                  <ArrowRight className="w-4 h-4 text-[#10B981] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
              </div>
            </motion.div>
          )}
          {mode === "claim" && (
            <motion.div key="claim" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Search & Claim Your Business</h2>
                <button onClick={() => { setMode(null); setClaimResults([]); setClaimSearch(""); }} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex gap-2 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                  <Input value={claimSearch} onChange={(e) => setClaimSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchBusinesses()}
                    placeholder="Search by business name..." className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]" data-testid="claim-search-input" />
                </div>
                <Button onClick={searchBusinesses} disabled={claimSearching || claimSearch.length < 2} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="claim-search-btn">
                  {claimSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
                </Button>
              </div>
              {claimResults.length > 0 ? (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {claimResults.map((biz) => (
                    <div key={biz.slug} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-colors">
                      <div>
                        <p className="text-white font-medium">{biz.name}</p>
                        <p className="text-sm text-[#94A3B8]">{[biz.city, biz.state].filter(Boolean).join(", ")}{biz.category_name && ` · ${biz.category_name}`}</p>
                      </div>
                      <Button size="sm" onClick={() => handleClaim(biz.slug)} disabled={claiming || biz.claim_status === "pending"} className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-sm" data-testid={`claim-btn-${biz.slug}`}>
                        {biz.claim_status === "pending" ? "Pending" : claiming ? <Loader2 className="w-3 h-3 animate-spin" /> : "Claim"}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : claimSearch.length >= 2 && !claimSearching ? (
                <div className="text-center py-8">
                  <p className="text-[#94A3B8]">No unclaimed businesses found for "{claimSearch}"</p>
                  <button onClick={() => setMode("create")} className="text-[#7C3AED] hover:text-[#8B5CF6] mt-2 text-sm">Create a new listing instead</button>
                </div>
              ) : null}
            </motion.div>
          )}
          {mode === "create" && (
            <motion.div key="create" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="bg-[#0F172A] border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Create New Listing</h2>
                <button onClick={() => { setMode(null); setNewListing(p => ({ ...p, is_individual: null })); }} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {newListing.is_individual === null ? (
                // Step 1: Pick listing type
                <div data-testid="listing-type-chooser">
                  <div className="text-center mb-6">
                    <h3 className="text-white font-semibold text-base mb-1">What are you listing?</h3>
                    <p className="text-sm text-[#94A3B8]">This helps customers find you in the right category and unlocks the Best of Best filter.</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setNewListing(p => ({ ...p, is_individual: false }))}
                      className="p-6 bg-white/5 border border-white/10 rounded-xl hover:border-[#10B981]/60 hover:bg-[#10B981]/5 transition-all text-left group"
                      data-testid="create-type-business"
                    >
                      <Building2 className="w-8 h-8 text-[#10B981] mb-3" />
                      <h4 className="text-white font-semibold mb-1">Business</h4>
                      <p className="text-sm text-[#94A3B8]">A company — e.g. roofing co., restaurant, dental practice, shop, or service provider.</p>
                      <ArrowRight className="w-4 h-4 text-[#10B981] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewListing(p => ({ ...p, is_individual: true }))}
                      className="p-6 bg-white/5 border border-white/10 rounded-xl hover:border-[#7C3AED]/60 hover:bg-[#7C3AED]/5 transition-all text-left group"
                      data-testid="create-type-pro"
                    >
                      <User className="w-8 h-8 text-[#7C3AED] mb-3" />
                      <h4 className="text-white font-semibold mb-1">Pro / Agent</h4>
                      <p className="text-sm text-[#94A3B8]">An individual professional — Realtor, Loan Officer, Insurance Agent, Coach, Consultant, etc.</p>
                      <ArrowRight className="w-4 h-4 text-[#7C3AED] mt-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-5 flex items-center gap-2 text-sm">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${newListing.is_individual ? "bg-[#7C3AED]/15 text-[#7C3AED]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
                      {newListing.is_individual ? <User className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
                      Listing as {newListing.is_individual ? "Pro / Agent" : "Business"}
                    </span>
                    <button onClick={() => setNewListing(p => ({ ...p, is_individual: null }))} className="text-xs text-[#64748B] hover:text-white underline" data-testid="change-listing-type-btn">change</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-1">{newListing.is_individual ? "Your Full Name *" : "Business Name *"}</label>
                      <Input value={newListing.name} onChange={(e) => setNewListing(p => ({ ...p, name: e.target.value }))} placeholder={newListing.is_individual ? "Jane Smith — Realtor" : "Your Business Name"} className="bg-white/5 border-white/10 text-white" data-testid="create-name" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Category</label>
                      <Input value={newListing.category_name} onChange={(e) => setNewListing(p => ({ ...p, category_name: e.target.value }))} placeholder={newListing.is_individual ? "e.g. Realtor, Loan Officer" : "e.g. Roofing, Restaurant"} className="bg-white/5 border-white/10 text-white" data-testid="create-category" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-1">City</label>
                      <Input value={newListing.city} onChange={(e) => setNewListing(p => ({ ...p, city: e.target.value }))} placeholder="City" className="bg-white/5 border-white/10 text-white" data-testid="create-city" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <Button onClick={handleCreate} disabled={creating || !newListing.name.trim()} className="bg-[#10B981] hover:bg-[#059669]" data-testid="create-submit-btn">
                      {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Listing</>}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // --- Pending Claim ---
  if (listing.claim_status === "pending") {
    return (
      <div className="space-y-6" data-testid="listing-management">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">My Listing</h1>
          <p className="text-[#94A3B8] mt-1">Manage your business listing</p>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] border border-[#F59E0B]/30 rounded-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-[#F59E0B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Claim Under Review</h2>
          <p className="text-[#94A3B8] max-w-lg mx-auto mb-4">
            Your claim for <span className="text-white font-medium">{listing.name}</span> is being reviewed.
          </p>
          <span className="inline-block px-4 py-2 bg-[#F59E0B]/10 text-[#F59E0B] rounded-lg text-sm font-medium">Status: Pending Review</span>
        </motion.div>
      </div>
    );
  }

  // =============================================
  // FULL LISTING EDITOR (Sales Funnel Builder)
  // =============================================
  // One-time prompt: if listing doesn't yet have is_individual set, ask which type.
  const needsTypePrompt = listing.is_individual === undefined || listing.is_individual === null;

  const chooseListingType = async (isIndividual) => {
    try {
      await axios.patch(`${API}/listings/${listing.id || listing.slug}`,
        { is_individual: isIndividual },
        { headers: getAuthHeader() }
      );
      setListing(prev => ({ ...prev, is_individual: isIndividual }));
      toast.success(`Listing type saved — ${isIndividual ? "Pro / Agent" : "Business"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save listing type");
    }
  };

  return (
    <div className="space-y-6" data-testid="listing-management">
      {/* One-time listing type prompt (shown until user picks) */}
      {needsTypePrompt && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#7C3AED]/15 to-[#00E5FF]/10 border border-[#7C3AED]/30 rounded-xl p-5"
          data-testid="listing-type-prompt"
        >
          <div className="flex items-start gap-3 mb-4">
            <Award className="w-5 h-5 text-[#FBBF24] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold mb-1">Help customers find you faster</h3>
              <p className="text-sm text-[#94A3B8]">Tell us if your listing is a Business or a Pro / Agent. This unlocks the right filters in the directory (Best of Best Business vs Best of Best Pros).</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => chooseListingType(false)} className="bg-[#10B981] hover:bg-[#059669]" data-testid="prompt-choose-business">
              <Building2 className="w-4 h-4 mr-2" /> I'm a Business
            </Button>
            <Button onClick={() => chooseListingType(true)} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="prompt-choose-pro">
              <User className="w-4 h-4 mr-2" /> I'm a Pro / Agent
            </Button>
          </div>
        </motion.div>
      )}
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">My Listing</h1>
          <p className="text-[#94A3B8] mt-1">Build your sales page — make your listing irresistible</p>
        </div>
        <div className="flex items-center gap-3">
          <a href={`/business/${listing.slug}`} target="_blank" rel="noopener noreferrer" data-testid="preview-listing-btn">
            <Button variant="outline" className="border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10 hover:border-[#00E5FF]/50">
              <ExternalLink className="w-4 h-4 mr-2" /> Preview My Listing
            </Button>
          </a>
          {listing.status === "published" ? (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-[#10B981]/20 text-[#10B981] rounded-lg text-sm" data-testid="listing-status"><CheckCircle className="w-4 h-4" /> Published</span>
          ) : (
            <span className="flex items-center gap-2 px-3 py-1.5 bg-[#F59E0B]/20 text-[#F59E0B] rounded-lg text-sm" data-testid="listing-status"><AlertCircle className="w-4 h-4" /> Draft</span>
          )}
          <Button onClick={() => set("status", listing.status === "published" ? "draft" : "published")} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="toggle-publish-btn">
            {listing.status === "published" ? <><Eye className="w-4 h-4 mr-2" /> Unpublish</> : <><Eye className="w-4 h-4 mr-2" /> Publish</>}
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6] disabled:opacity-50" data-testid="save-listing-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save</>}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-[#7C3AED] text-white" : "text-[#94A3B8] hover:bg-white/5 hover:text-white"}`}
            data-testid={`tab-${tab.id}`}>
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* BRAND UPDATE BANNER — appears when Brandfetch detects a change on the business's website */}
      {brandUpdateNotice?.has_updates && (
        <div className="rounded-xl border border-[#00E5FF]/30 bg-gradient-to-r from-[#00E5FF]/10 to-[#7C3AED]/10 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="brand-update-banner">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Sparkles className="w-5 h-5 text-[#00E5FF] flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm">Your website branding looks different.</p>
              <p className="text-[#94A3B8] text-xs">
                We detected changes on <span className="text-white">{brandUpdateNotice.domain}</span>
                {brandUpdateNotice.changes?.brand_primary_color?.fresh && (
                  <> · new brand color
                    <span className="inline-block w-3 h-3 rounded-full ml-1 align-middle border border-white/20"
                      style={{ background: brandUpdateNotice.changes.brand_primary_color.fresh }} />
                  </>
                )}
                {brandUpdateNotice.changes?.logo_url?.fresh && <> · new logo</>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-white/15 text-[#94A3B8] hover:text-white"
              onClick={() => {
                try { localStorage.removeItem(`lb_brandcheck_${listing.id}`); } catch (e) { console.warn("brandcheck cache clear failed:", e); }
                setBrandUpdateNotice(null);
              }}
              data-testid="brand-update-dismiss-btn"
            >
              Dismiss
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setActiveTab("branding");
                setBrandUpdateNotice(null);
                runAutoImport({ force: true });
              }}
              className="bg-gradient-to-r from-[#00E5FF] to-[#7C3AED] text-white"
              data-testid="brand-update-reimport-btn"
            >
              <Sparkles className="w-4 h-4 mr-1.5" /> Re-import
            </Button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#0F172A] border border-white/10 rounded-xl p-6">

        {/* ===== HORSEPOWER ===== */}
        {activeTab === "horsepower" && (
          <HorsepowerDashboard slug={listing.slug} />
        )}

        {/* ===== BRANDING ===== */}
        {activeTab === "branding" && (
          <div className="space-y-8">
            {/* AUTO-IMPORT CARD */}
            <div className="rounded-2xl border border-[#7C3AED]/30 bg-gradient-to-br from-[#7C3AED]/10 to-transparent p-5" data-testid="auto-import-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
                    <Sparkles className="w-5 h-5 text-[#7C3AED]" /> Auto-import branding from your website
                  </h3>
                  <p className="text-sm text-[#94A3B8]">
                    Grabs your logo, brand colors, About Us, and hero images automatically.
                    {!listing.website && <span className="text-[#F59E0B]"> Enter your website URL below first.</span>}
                  </p>
                </div>
                <Button
                  onClick={() => runAutoImport()}
                  disabled={autoImporting || !listing.website}
                  className="bg-gradient-to-r from-[#7C3AED] to-[#00E5FF] hover:from-[#00E5FF] hover:to-[#7C3AED] text-white"
                  data-testid="auto-import-btn"
                >
                  {autoImporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Auto-import</>)}
                </Button>
              </div>
              {/* Inline website URL input — saves the user from bouncing to Contact & Hours tab */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <label className="text-xs text-[#94A3B8] flex-shrink-0">Website URL</label>
                <Input
                  value={listing.website || ""}
                  onChange={(e) => set("website", e.target.value)}
                  placeholder="https://yourbusiness.com"
                  className="bg-white/5 border-white/10 text-white flex-1 min-w-[240px]"
                  data-testid="auto-import-website-input"
                />
                {listing.website && (
                  <Button
                    size="sm"
                    onClick={() => runAutoImport({ force: true })}
                    disabled={autoImporting}
                    variant="outline"
                    className="border-white/15 text-[#94A3B8] hover:text-white"
                    data-testid="auto-import-force-btn"
                    title="Overwrite existing logo, About Us and colors"
                  >
                    Re-import (force)
                  </Button>
                )}
              </div>
              {hasChanges && (
                <p className="mt-2 text-xs text-[#F59E0B]">
                  Click <span className="text-white font-medium">Save</span> (top right) to persist your website URL before auto-importing.
                </p>
              )}
              {autoImportResult && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs" data-testid="auto-import-result">
                  {autoImportResult.brand?.logo_url && (
                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-[#64748B] mb-1">Logo</p>
                      <img src={autoImportResult.brand.logo_url} alt="brand logo" className="h-10 w-auto object-contain" />
                    </div>
                  )}
                  {autoImportResult.brand?.brand_primary_color && (
                    <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                      <p className="text-[#64748B] mb-1">Brand Color</p>
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded border border-white/20" style={{ background: autoImportResult.brand.brand_primary_color }} />
                        <span className="text-white font-mono">{autoImportResult.brand.brand_primary_color}</span>
                      </div>
                    </div>
                  )}
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-[#64748B] mb-1">Images Imported</p>
                    <p className="text-white font-semibold">{(autoImportResult.scraped?.images || []).length}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <p className="text-[#64748B] mb-1">About Us</p>
                    <p className="text-white font-semibold">{autoImportResult.about_us_html ? "Generated" : "Skipped"}</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Business Identity</h2>
              <p className="text-sm text-[#94A3B8] mb-6">Your logo and cover image are the first impression visitors see</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-3">Logo</label>
                  <FileUpload category="logo" accept="image/*" label="Upload Logo" currentUrl={listing.logo_url}
                    onUploaded={(url) => set("logo_url", url)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Cover / Background Image</label>
                  <p className="text-xs text-[#64748B] mb-3">
                    Recommended: <span className="text-[#A78BFA] font-medium">1920×640 px</span> · JPG/WebP · under 500 KB.
                    Keep subject in the top 2/3 — bottom fades into the page.
                  </p>
                  {listing.background_image_is_default && (
                    <div className="mb-3 px-3 py-2 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/30 text-xs text-[#C7D2E0] flex items-start gap-2">
                      <span className="text-[#A78BFA] mt-0.5">✨</span>
                      <span>
                        We auto-applied a free <strong className="text-white">{listing.category_name}</strong> template so your page looks great right away.
                        Upload your own cover any time to replace it.
                      </span>
                    </div>
                  )}
                  <FileUpload category="background" accept="image/*" label="Upload Cover Image" currentUrl={listing.background_image_url}
                    onUploaded={(url) => { set("background_image_url", url); set("background_image_is_default", false); }} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">{listing.is_individual ? "Full Name *" : "Business Name *"}</label>
                <Input value={listing.name || ""} onChange={(e) => set("name", e.target.value)} className="bg-white/5 border-white/10 text-white" data-testid="listing-name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Category</label>
                <Input value={listing.category_name || ""} onChange={(e) => set("category_name", e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            {/* URL slug + AI Hero Name — power user fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  Page URL <span className="text-[#64748B] font-normal">(slug)</span>
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-[#64748B] text-sm whitespace-nowrap">/business/</span>
                  <Input
                    value={listing.slug || ""}
                    onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/-{2,}/g, "-"))}
                    placeholder="my-business-name"
                    className="bg-white/5 border-white/10 text-white font-mono text-sm"
                    data-testid="listing-slug"
                  />
                </div>
                <p className="text-xs text-[#64748B] mt-1">Lowercase letters, numbers, and hyphens only. Changing this updates your public URL.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                  AI Hero / Chat Name
                </label>
                <Input
                  value={listing.ai_agent_name || ""}
                  onChange={(e) => set("ai_agent_name", e.target.value)}
                  placeholder="e.g. Hero-LocalBest"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="listing-hero-name"
                />
                <p className="text-xs text-[#64748B] mt-1">The name your AI assistant introduces itself with on this listing.</p>
              </div>
            </div>
            {/* Listing type toggle — always editable */}
            <div className="rounded-lg bg-white/5 border border-white/10 p-4" data-testid="listing-type-toggle">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-white">Listing Type</p>
                  <p className="text-xs text-[#94A3B8]">This drives the directory filters (Best of Best Business vs Best of Best Pros).</p>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => set("is_individual", false)}
                    className={`px-3 py-1.5 rounded-md text-sm transition flex items-center gap-1.5 ${!listing.is_individual ? "bg-[#10B981] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                    data-testid="toggle-type-business"
                  >
                    <Building2 className="w-3.5 h-3.5" /> Business
                  </button>
                  <button
                    type="button"
                    onClick={() => set("is_individual", true)}
                    className={`px-3 py-1.5 rounded-md text-sm transition flex items-center gap-1.5 ${listing.is_individual ? "bg-[#7C3AED] text-white" : "bg-white/5 text-[#94A3B8] hover:bg-white/10"}`}
                    data-testid="toggle-type-pro"
                  >
                    <User className="w-3.5 h-3.5" /> Pro / Agent
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Tagline / Short Description</label>
              <Input value={listing.short_description || listing.description_short || ""} onChange={(e) => set("short_description", e.target.value)}
                placeholder="One-liner that hooks visitors (160 chars)" maxLength={160} className="bg-white/5 border-white/10 text-white" />
              <p className="text-xs text-[#64748B] mt-1">{(listing.short_description || listing.description_short || "").length}/160</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                Hero Tagline (shown under your name on the listing)
              </label>
              <textarea
                value={listing.seo_description || ""}
                onChange={(e) => set("seo_description", e.target.value)}
                placeholder="This is the gray italic line that appears directly under your business name on your public listing. Write 1–3 sentences that describe what you do and where you serve."
                rows={4}
                maxLength={500}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]"
                data-testid="seo-description-textarea"
              />
              <p className="text-xs text-[#64748B] mt-1">
                {(listing.seo_description || "").length}/500 — overwrites the AI-generated description shown under your name.
              </p>
            </div>

            {/* Best of Best Press Release — only relevant when listing is approved as Best of Best */}
            {listing.is_best_of_best && (
              <div className="rounded-2xl border border-[#F4B41A]/30 bg-gradient-to-br from-[#F4B41A]/8 to-transparent p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-[#F4B41A]">★</span>
                  <h3 className="text-white font-semibold">Best of Best Press Release</h3>
                </div>
                <p className="text-xs text-[#94A3B8]">
                  Renders directly below the Cinematic Video on your public listing.
                  Image displays as the spotlight header, body shows underneath in
                  press-release format. Both fields are optional.
                </p>

                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                    Press Release Header Image
                  </label>

                  <div className="space-y-3">
                    <FileUpload
                      category="press_release"
                      accept="image/*"
                      label="Upload Press Release Image"
                      currentUrl={listing.press_release_image}
                      onUploaded={(url) => set("press_release_image", url)}
                    />
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#64748B]">
                      <span className="flex-1 h-px bg-white/10" />
                      <span>or paste a URL</span>
                      <span className="flex-1 h-px bg-white/10" />
                    </div>
                    <Input
                      value={listing.press_release_image || ""}
                      onChange={(e) => set("press_release_image", e.target.value)}
                      placeholder="https://… paste your spotlight graphic URL"
                      className="bg-white/5 border-white/10 text-white"
                      data-testid="press-release-image-input"
                    />
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">
                    Recommended: 1920×1080 or square 1:1 — gets a gold-glow frame.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-2">
                    Press Release Body
                  </label>
                  <textarea
                    value={listing.press_release_body || ""}
                    onChange={(e) => set("press_release_body", e.target.value)}
                    rows={14}
                    placeholder={`Paste the full press release here.\n\nUse # for the main headline, ## for sub-headlines, and **bold** for emphasis.\n\nExample:\n\n# LocalBest.ai Names {Business} the "Best of Best…" of {City}\n\n## A spotlight campaign with $X in monthly rewards…\n\n**CITY, STATE —** LocalBest.ai is proud to announce…`}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#F4B41A] font-mono text-sm leading-relaxed"
                    data-testid="press-release-body-textarea"
                  />
                  <p className="text-xs text-[#64748B] mt-1">
                    {(listing.press_release_body || "").length} characters · Supports
                    # / ## / ### headings and **bold** text.
                  </p>
                </div>
              </div>
            )}

            {/* Hidden message when not Best of Best yet */}
            {!listing.is_best_of_best && (
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <p className="text-xs text-[#94A3B8]">
                  <span className="text-[#F4B41A] font-semibold">★ Best of Best Press Release</span> —
                  This optional spotlight section unlocks once your listing is approved
                  as a Best of Best business.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ===== ABOUT US ===== */}
        {activeTab === "about" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Tell Your Story</h2>
              <p className="text-sm text-[#94A3B8] mb-6">This is your elevator pitch — why should customers choose you?</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">About Your Business</label>
              <textarea value={listing.about_us || ""} onChange={(e) => set("about_us", e.target.value)} placeholder="Share your story, mission, and what makes you unique..."
                rows={10} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" data-testid="about-us-textarea" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Full Description</label>
              <textarea value={listing.description || listing.description_long || ""} onChange={(e) => set("description", e.target.value)} placeholder="Detailed description of your services, expertise, and value..."
                rows={6} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Introduction Video URL</label>
              <Input value={listing.video_url || ""} onChange={(e) => set("video_url", e.target.value)} placeholder="https://youtube.com/watch?v=... or direct video URL"
                className="bg-white/5 border-white/10 text-white" />
              <p className="text-xs text-[#64748B] mt-1">A video intro can increase conversion by 80%</p>
            </div>
          </div>
        )}

        {/* ===== OFFERS & PROMOTIONS ===== */}
        {activeTab === "offers" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white mb-1">Offers & Promotions</h2>
                <p className="text-sm text-[#94A3B8]">Showcase your deals — images, videos, and descriptions to convert visitors</p>
              </div>
              <Button onClick={addOffer} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="add-offer-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Offer
              </Button>
            </div>
            {(listing.offers || []).length === 0 && (
              <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-xl">
                <Tag className="w-12 h-12 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#94A3B8]">No offers yet. Add your first promotion to attract more customers.</p>
              </div>
            )}
            <div className="space-y-6">
              {(listing.offers || []).map((offer, idx) => (
                <motion.div key={offer.id || idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-5 relative">
                  <button onClick={() => removeOffer(idx)} className="absolute top-3 right-3 text-[#64748B] hover:text-red-400" data-testid={`remove-offer-${idx}`}>
                    <X className="w-5 h-5" />
                  </button>
                  <p className="text-xs text-[#7C3AED] font-medium mb-4">OFFER #{idx + 1}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Offer Title</label>
                      <Input value={offer.title || ""} onChange={(e) => updateOffer(idx, "title", e.target.value)} placeholder="e.g. 20% Off First Visit" className="bg-white/5 border-white/10 text-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Offer Link (optional)</label>
                      <Input value={offer.link || ""} onChange={(e) => updateOffer(idx, "link", e.target.value)} placeholder="https://..." className="bg-white/5 border-white/10 text-white" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Description</label>
                    <textarea value={offer.description || ""} onChange={(e) => updateOffer(idx, "description", e.target.value)} placeholder="Describe this offer..." rows={3}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Offer Image</label>
                      <FileUpload category="offers" accept="image/*" label="Upload Image" currentUrl={offer.image_url}
                        onUploaded={(url) => updateOffer(idx, "image_url", url)} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Offer Video (optional)</label>
                      <FileUpload category="offers" accept="video/*" label="Upload Video" currentUrl={offer.video_url}
                        onUploaded={(url) => updateOffer(idx, "video_url", url)} />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* ===== GALLERY ===== */}
        {activeTab === "gallery" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Media Gallery</h2>
              <p className="text-sm text-[#94A3B8] mb-2">Showcase your work — photos and landscape videos that build trust. For mobile/vertical videos, use the <span className="text-[#7C3AED] font-medium">Shorts</span> tab.</p>
              <p className="text-xs text-[#64748B] mb-4 flex items-center gap-1.5"><GripVertical className="w-3.5 h-3.5 text-[#A78BFA]" /> Drag the grip handle to reorder · click <X className="w-3 h-3 inline" /> or the arrows to remove or move on mobile.</p>
              <div className="mb-6">
                <MediaUrlAdder
                  acceptType="any"
                  buttonLabel="Add Video / Audio by URL"
                  onAdd={(item) => set("gallery", [...(listing.gallery || []), item])}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <DndContext
                sensors={shortsSensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const items = listing.gallery || [];
                  const oldIdx = items.findIndex((it, i) => (it.id || `g-${i}`) === active.id);
                  const newIdx = items.findIndex((it, i) => (it.id || `g-${i}`) === over.id);
                  if (oldIdx < 0 || newIdx < 0) return;
                  set("gallery", arrayMove(items, oldIdx, newIdx));
                }}
              >
                <SortableContext
                  items={(listing.gallery || []).map((it, i) => it.id || `g-${i}`)}
                  strategy={rectSortingStrategy}
                >
                  {(listing.gallery || []).map((item, idx) => (
                    <SortableGalleryItem
                      key={item.id || `g-${idx}`}
                      id={item.id || `g-${idx}`}
                      item={item}
                      idx={idx}
                      total={(listing.gallery || []).length}
                      onRemove={() => removeGalleryItem(idx)}
                      onMove={(dir) => {
                        const items = listing.gallery || [];
                        const newIdx = idx + dir;
                        if (newIdx < 0 || newIdx >= items.length) return;
                        set("gallery", arrayMove(items, idx, newIdx));
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <FileUpload category="gallery" accept="image/*,video/*" label="Add to Gallery"
                onUploaded={addGalleryItem} resetAfterUpload className="min-h-[10rem]" />
            </div>
          </div>
        )}

        {/* ===== SHORTS (vertical / mobile videos) ===== */}
        {activeTab === "shorts" && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                  Shorts & Reels
                  <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white">Vertical 9:16</span>
                </h2>
                <p className="text-sm text-[#94A3B8]">Mobile vertical videos (like TikTok / Reels / YouTube Shorts). These appear in a dedicated swipeable Shorts section on your public listing — perfect for short-form content.</p>
              </div>
              <ShortRecorder
                onUploaded={(url, data) => {
                  if (!url) return;
                  const shorts = [...(listing.shorts || []), {
                    id: data?.file_id || Date.now().toString(),
                    url,
                    caption: data?.filename || "",
                    type: "video",
                    provider: "upload",
                  }];
                  set("shorts", shorts);
                }}
              />
              <MediaUrlAdder
                acceptType="video"
                buttonLabel="Add Short by URL"
                onAdd={(item) => set("shorts", [...(listing.shorts || []), item])}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <DndContext
                sensors={shortsSensors}
                collisionDetection={closestCenter}
                onDragEnd={(event) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;
                  const items = listing.shorts || [];
                  const oldIdx = items.findIndex((it, i) => (it.id || `s-${i}`) === active.id);
                  const newIdx = items.findIndex((it, i) => (it.id || `s-${i}`) === over.id);
                  if (oldIdx < 0 || newIdx < 0) return;
                  set("shorts", arrayMove(items, oldIdx, newIdx));
                }}
              >
                <SortableContext
                  items={(listing.shorts || []).map((it, i) => it.id || `s-${i}`)}
                  strategy={rectSortingStrategy}
                >
                  {(listing.shorts || []).map((item, idx) => (
                    <SortableShortCard
                      key={item.id || `s-${idx}`}
                      id={item.id || `s-${idx}`}
                      item={item}
                      idx={idx}
                      onRemove={() => set("shorts", (listing.shorts || []).filter((_, i) => i !== idx))}
                      onCaption={async () => {
                        try {
                          const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;
                          toast.message("Generating captions…");
                          const res = await axios.post(`${API_ROOT}/shorts/generate-caption`, {
                            business_id: listing.id,
                            short_url: item.url,
                            short_filename: item.caption || "",
                          });
                          const caps = res.data?.captions || [];
                          if (!caps.length) { toast.error("No captions generated"); return; }
                          const text = caps.map((c, i) => `Option ${i + 1}:\n${c.caption}\n${(c.hashtags || []).join(' ')}`).join('\n\n');
                          await navigator.clipboard?.writeText(text);
                          toast.success(`${caps.length} captions copied to clipboard`);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Caption generation failed");
                        }
                      }}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              <FileUpload
                category="shorts"
                accept="video/*"
                label="Upload a Short / Reel"
                resetAfterUpload
                className="min-h-[16rem] aspect-[9/16]"
                onUploaded={(url, data) => {
                  if (!url) return;
                  const shorts = [...(listing.shorts || []), {
                    id: data?.file_id || Date.now().toString(),
                    url,
                    caption: data?.filename || "",
                    type: "video",
                  }];
                  set("shorts", shorts);
                }}
              />
            </div>
          </div>
        )}

        {/* ===== SOCIAL & EMBEDS ===== */}
        {activeTab === "social" && (
          <div className="space-y-8">
            {/* Social Links */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Social Media</h2>
              <p className="text-sm text-[#94A3B8] mb-6">Connect all your social channels</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { key: "facebook", label: "Facebook Page URL", ph: "https://facebook.com/yourbusiness" },
                  { key: "instagram", label: "Instagram", ph: "https://instagram.com/yourbusiness" },
                  { key: "tiktok", label: "TikTok", ph: "https://tiktok.com/@yourbusiness" },
                  { key: "youtube", label: "YouTube Channel", ph: "https://youtube.com/@yourbusiness" },
                  { key: "linkedin", label: "LinkedIn", ph: "https://linkedin.com/company/yourbusiness" },
                  { key: "twitter", label: "X (Twitter)", ph: "https://x.com/yourbusiness" },
                  { key: "yelp", label: "Yelp", ph: "https://yelp.com/biz/yourbusiness" },
                  { key: "google_business", label: "Google Business", ph: "https://g.page/yourbusiness" },
                ].map((s) => (
                  <div key={s.key}>
                    <label className="block text-sm font-medium text-[#E2E8F0] mb-1">{s.label}</label>
                    <Input value={listing.social_links?.[s.key] || ""} onChange={(e) => setNested("social_links", s.key, e.target.value)}
                      placeholder={s.ph} className="bg-white/5 border-white/10 text-white" />
                  </div>
                ))}
              </div>
            </div>

            {/* Podcast Link */}
            <div className="border-t border-white/10 pt-6">
              <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-[#FBBF24]" /> Podcast Link
              </h2>
              <p className="text-sm text-[#94A3B8] mb-4">
                Send visitors to your podcast — recommend businesses, interview owners, talk about your community, and grow your audience.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="podcast-link-fields">
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Podcast title</label>
                  <Input
                    value={listing.podcast_title || ""}
                    onChange={(e) => set("podcast_title", e.target.value)}
                    placeholder="e.g. Best of Tampa with Mike"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="podcast-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Platform (optional)</label>
                  <select
                    value={listing.podcast_platform || ""}
                    onChange={(e) => set("podcast_platform", e.target.value || null)}
                    className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white text-sm px-3 focus:outline-none focus:border-[#7C3AED]"
                    data-testid="podcast-platform-select"
                  >
                    <option value="" className="bg-[#0F172A]">Auto / Other</option>
                    <option value="spotify" className="bg-[#0F172A]">Spotify</option>
                    <option value="apple" className="bg-[#0F172A]">Apple Podcasts</option>
                    <option value="youtube" className="bg-[#0F172A]">YouTube Podcast</option>
                    <option value="podbean" className="bg-[#0F172A]">Podbean</option>
                    <option value="buzzsprout" className="bg-[#0F172A]">Buzzsprout</option>
                    <option value="amazon" className="bg-[#0F172A]">Amazon Music</option>
                    <option value="iheart" className="bg-[#0F172A]">iHeart</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Podcast URL</label>
                  <Input
                    value={listing.podcast_url || ""}
                    onChange={(e) => set("podcast_url", e.target.value)}
                    placeholder="https://open.spotify.com/show/…"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="podcast-url-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Short description (optional)</label>
                  <textarea
                    value={listing.podcast_description || ""}
                    onChange={(e) => set("podcast_description", e.target.value)}
                    rows={2}
                    placeholder="What's your podcast about? Who's it for?"
                    className="w-full rounded-md bg-white/5 border border-white/10 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#7C3AED]"
                    data-testid="podcast-description-input"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#E2E8F0] mb-1">Cover image URL (optional — falls back to your logo)</label>
                  <Input
                    value={listing.podcast_image || ""}
                    onChange={(e) => set("podcast_image", e.target.value)}
                    placeholder="https://…"
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="podcast-image-input"
                  />
                </div>
              </div>
            </div>

            {/* Facebook Page URL */}
            <div className="border-t border-white/10 pt-6">
              <h2 className="text-lg font-semibold text-white mb-1">Facebook Page URL</h2>
              <p className="text-sm text-[#94A3B8] mb-4">
                Paste the public Facebook Business Page URL. We'll automatically display a Facebook feed in your listing's right column. Profiles, groups, reels, and events are not supported.
              </p>
              <Input value={listing.facebook_embed || ""} onChange={(e) => set("facebook_embed", e.target.value)}
                placeholder="https://www.facebook.com/yourbusiness" className="bg-white/5 border-white/10 text-white" data-testid="facebook-embed-input" />
              {listing.facebook_embed && (() => {
                const fbUrl = normalizeFbUrl(listing.facebook_embed);
                if (!fbUrl) {
                  return (
                    <p className="mt-3 text-xs text-[#F59E0B]">
                      ⚠ This doesn't look like a public Facebook Page URL. Use the format <code className="text-white">https://www.facebook.com/PageName</code> (profiles, groups, and reels are not supported).
                    </p>
                  );
                }
                return (
                  <div className="mt-4 bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-[#10B981] mb-2 flex items-center gap-1.5">
                      <CheckCircle className="w-3.5 h-3.5" /> Valid Facebook Page URL — Preview:
                    </p>
                    <iframe title="Facebook Page" src={`https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(fbUrl)}&tabs=timeline&width=500&height=400&small_header=false&adapt_container_width=true`}
                      style={{ border: "none", overflow: "hidden", maxWidth: 500, width: "100%", height: 400 }} className="rounded-lg" scrolling="no"
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
                  </div>
                );
              })()}
            </div>

            {/* YouTube Embed */}
            <div className="border-t border-white/10 pt-6">
              <h2 className="text-lg font-semibold text-white mb-1">YouTube Channel Embed</h2>
              <p className="text-sm text-[#94A3B8] mb-4">Paste a YouTube video or channel URL</p>
              <Input value={listing.youtube_embed || ""} onChange={(e) => set("youtube_embed", e.target.value)}
                placeholder="https://www.youtube.com/watch?v=... or channel URL" className="bg-white/5 border-white/10 text-white" data-testid="youtube-embed-input" />
              {listing.youtube_embed && (() => {
                const match = listing.youtube_embed.match(/(?:watch\?v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]+)/);
                const videoId = match?.[1];
                return videoId ? (
                  <div className="mt-4 bg-white/5 rounded-lg p-4">
                    <p className="text-xs text-[#64748B] mb-2">Preview:</p>
                    <iframe title="YouTube" src={`https://www.youtube.com/embed/${videoId}`} width="100%" height="315"
                      className="rounded-lg" style={{ border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  </div>
                ) : null;
              })()}
            </div>

            {/* Custom Embeds */}
            <div className="border-t border-white/10 pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white mb-1">Custom Embeds</h2>
                  <p className="text-sm text-[#94A3B8]">Add booking widgets, reviews, maps, or any HTML embed</p>
                </div>
                <Button onClick={addEmbed} variant="outline" className="border-white/20 text-white hover:bg-white/5" data-testid="add-embed-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Embed
                </Button>
              </div>
              {(listing.custom_embeds || []).map((embed, idx) => (
                <div key={embed.id || idx} className="mb-4 bg-white/5 border border-white/10 rounded-xl p-4 relative">
                  <button onClick={() => removeEmbed(idx)} className="absolute top-3 right-3 text-[#64748B] hover:text-red-400"><X className="w-4 h-4" /></button>
                  <Input value={embed.title || ""} onChange={(e) => updateEmbed(idx, "title", e.target.value)} placeholder="Embed title (e.g. Booking Widget)" className="bg-white/5 border-white/10 text-white mb-3" />
                  <textarea value={embed.code || ""} onChange={(e) => updateEmbed(idx, "code", e.target.value)} placeholder="Paste embed code (HTML/iframe)..." rows={4}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white font-mono text-sm placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== CONTACT & HOURS ===== */}
        {activeTab === "contact" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white mb-1">Contact Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Primary Phone</label>
                <Input value={listing.contact_phone || listing.phone_primary || ""} onChange={(e) => set("contact_phone", e.target.value)} placeholder="(555) 123-4567" className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Secondary Phone</label>
                <Input value={listing.phone_secondary || ""} onChange={(e) => set("phone_secondary", e.target.value)} placeholder="(555) 987-6543" className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Email</label>
                <Input type="email" value={listing.contact_email || listing.email || ""} onChange={(e) => set("contact_email", e.target.value)} placeholder="contact@business.com" className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Website</label>
                <Input value={listing.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://yourbusiness.com" className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div className="border-t border-white/10 pt-6">
              <h2 className="text-lg font-semibold text-white mb-4">Business Hours</h2>
              <div className="space-y-3">
                {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                  <div key={day} className="flex items-center gap-4">
                    <span className="w-24 text-sm text-[#E2E8F0] capitalize">{day}</span>
                    <Input value={listing.hours?.[day]?.open || ""} onChange={(e) => setNested("hours", day, { ...(listing.hours?.[day] || {}), open: e.target.value })}
                      placeholder="9:00 AM" className="bg-white/5 border-white/10 text-white w-32" />
                    <span className="text-[#64748B]">to</span>
                    <Input value={listing.hours?.[day]?.close || ""} onChange={(e) => setNested("hours", day, { ...(listing.hours?.[day] || {}), close: e.target.value })}
                      placeholder="5:00 PM" className="bg-white/5 border-white/10 text-white w-32" />
                    <label className="flex items-center gap-2 text-sm text-[#94A3B8] cursor-pointer">
                      <input type="checkbox" checked={listing.hours?.[day]?.closed || false}
                        onChange={(e) => setNested("hours", day, { ...(listing.hours?.[day] || {}), closed: e.target.checked })}
                        className="rounded border-white/20 bg-white/5 text-[#7C3AED]" />
                      Closed
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ===== LOCATION ===== */}
        {activeTab === "location" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white mb-1">Business Location</h2>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Street Address</label>
              <Input value={listing.street_address || listing.address || ""} onChange={(e) => set("street_address", e.target.value)} placeholder="123 Main Street" className="bg-white/5 border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">City</label>
                <Input value={listing.city || ""} onChange={(e) => set("city", e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">State</label>
                <Input value={listing.state || ""} onChange={(e) => set("state", e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">ZIP Code</label>
                <Input value={listing.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#E2E8F0] mb-2">Service Areas</label>
              <Input value={(listing.service_areas || []).join(", ")} onChange={(e) => set("service_areas", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                placeholder="Chicago, Naperville, Aurora (comma separated)" className="bg-white/5 border-white/10 text-white" />
            </div>
          </div>
        )}

        {/* ===== CALL TO ACTION ===== */}
        {activeTab === "cta" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-white mb-1">Call to Action</h2>
              <p className="text-sm text-[#94A3B8] mb-6">Configure the main CTA button on your listing — drive leads and conversions</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">CTA Button Text</label>
                <Input value={listing.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} placeholder="e.g. Get Free Quote, Book Now, Call Us" className="bg-white/5 border-white/10 text-white" data-testid="cta-text" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">CTA Link URL</label>
                <Input value={listing.cta_url || ""} onChange={(e) => set("cta_url", e.target.value)} placeholder="https://yourbooking.com or leave blank for phone" className="bg-white/5 border-white/10 text-white" data-testid="cta-url" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#E2E8F0] mb-2">CTA Phone Number</label>
                <Input value={listing.cta_phone || ""} onChange={(e) => set("cta_phone", e.target.value)} placeholder="(555) 123-4567" className="bg-white/5 border-white/10 text-white" data-testid="cta-phone" />
                <p className="text-xs text-[#64748B] mt-1">If no URL is set, the CTA button will initiate a phone call</p>
              </div>
            </div>
            {/* Preview */}
            {(listing.cta_text) && (
              <div className="border-t border-white/10 pt-6">
                <p className="text-xs text-[#64748B] mb-3">Preview:</p>
                <div className="bg-gradient-to-r from-[#7C3AED] to-[#EC4899] p-8 rounded-xl text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">Ready to get started?</h3>
                  <button className="px-8 py-3 bg-white text-[#7C3AED] font-bold rounded-full hover:bg-gray-100 transition-colors text-lg">
                    {listing.cta_text || "Get Started"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== WEBSITE BADGE ===== */}
        {activeTab === "badge" && (
          <WebsiteBadgeTab slug={listing.slug} />
        )}

        {/* ===== SOCIAL PREVIEW ===== */}
        {activeTab === "social-preview" && (
          <SocialPreviewTab slug={listing.slug} />
        )}
      </motion.div>

      {/* ===== DANGER ZONE — Delete My Listing ===== */}
      <div className="mt-12 border-t border-white/10 pt-8" data-testid="danger-zone">
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-5 md:p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-semibold">Delete this listing</h3>
                <p className="text-sm text-[#94A3B8] mt-1">
                  Permanently remove your listing and start over with a clean slate. This deletes your branding,
                  gallery, offers, reviews, leads and analytics for <span className="text-white font-medium">{listing.name || listing.slug}</span>.
                  This action cannot be undone.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-500/15 hover:bg-red-500/25 text-red-300 hover:text-red-200 border border-red-500/40 self-start md:self-auto"
              data-testid="delete-listing-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Delete Listing
            </Button>
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => !deleting && setShowDeleteConfirm(false)}
            data-testid="delete-listing-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-lg bg-[#0F172A] border border-red-500/30 rounded-2xl p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/15 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Delete your listing?</h3>
                  <p className="text-sm text-[#94A3B8] mt-1">
                    You're about to permanently delete <span className="text-white font-medium">{listing.name || listing.slug}</span>.
                    All content, gallery media, leads, reviews and analytics will be removed. This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-4">
                <p className="text-xs text-[#94A3B8] mb-2">Type <span className="text-red-300 font-mono font-bold">DELETE</span> to confirm:</p>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="bg-white/5 border-white/10 text-white font-mono"
                  autoFocus
                  data-testid="delete-confirm-input"
                />
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(""); }}
                  disabled={deleting}
                  className="border-white/20 text-white hover:bg-white/5"
                  data-testid="cancel-delete-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteListing}
                  disabled={deleting || deleteConfirmText.trim().toUpperCase() !== "DELETE"}
                  className="bg-red-600 hover:bg-red-500 text-white disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" /> Permanently delete</>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Save Bar */}
      {hasChanges && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1E293B] border border-white/10 rounded-xl p-4 shadow-2xl flex items-center gap-4 z-50">
          <p className="text-white text-sm">You have unsaved changes</p>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6]" data-testid="floating-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
          </Button>
          <a href={`/business/${listing.slug}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="border-[#00E5FF]/30 text-[#00E5FF] hover:bg-[#00E5FF]/10">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Preview
            </Button>
          </a>
        </motion.div>
      )}
    </div>
  );
}

/* ---- Sortable Short Card ---- */
function SortableShortCard({ id, item, idx, onRemove, onCaption }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-xl overflow-hidden bg-black aspect-[9/16] border border-white/10"
      data-testid={`sortable-short-${idx}`}
    >
      {(item.provider === "youtube" || item.provider === "youtube_shorts" || item.provider === "vimeo") && item.embed_url ? (
        <iframe
          src={item.embed_url}
          title={item.title || `Short ${idx + 1}`}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full pointer-events-none"
          style={{ border: 0 }}
        />
      ) : item.url ? (
        <video src={item.url} className="w-full h-full object-cover pointer-events-none" playsInline />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-[#94A3B8] text-xs p-3 bg-[#1E293B]">
          <X className="w-5 h-5 mb-1 text-red-400" />
          <span>Broken or missing media — delete & re-add</span>
        </div>
      )}
      {/* Drag handle — top-left */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-2 left-2 w-7 h-7 bg-black/70 hover:bg-[#7C3AED]/80 rounded-full flex items-center justify-center text-white opacity-90 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition z-10 touch-none"
        aria-label="Drag to reorder"
        title="Drag to reorder"
        data-testid={`drag-short-${idx}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      {/* Position indicator badge */}
      <span className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition z-10">
        #{idx + 1}
      </span>
      {/* Remove */}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-2 right-2 w-7 h-7 bg-black/70 hover:bg-red-500/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition z-10"
        data-testid={`remove-short-${idx}`}
        aria-label="Remove"
      >
        <X className="w-4 h-4 text-white" />
      </button>
      {/* Caption */}
      <button
        type="button"
        onClick={onCaption}
        className="absolute bottom-2 left-2 right-2 px-2 py-1.5 rounded-md bg-gradient-to-r from-[#EC4899] to-[#7C3AED] text-white text-[11px] font-semibold flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition z-10"
        data-testid={`caption-short-${idx}`}
      >
        <Sparkles className="w-3 h-3" /> Auto-Caption
      </button>
    </div>
  );
}

// Sortable Gallery item — drag handle + always-visible remove button + arrow-button reordering
function SortableGalleryItem({ id, item, idx, total, onRemove, onMove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="relative group rounded-lg overflow-hidden bg-[#1E293B] border border-white/10"
      data-testid={`sortable-gallery-${idx}`}
    >
      {(item.provider === "youtube" || item.provider === "vimeo") && item.embed_url ? (
        <div className="w-full h-40 bg-black">
          <iframe src={item.embed_url} title={item.title || `Media ${idx + 1}`} className="w-full h-full pointer-events-none" style={{ border: 0 }} allowFullScreen />
        </div>
      ) : item.provider === "audio" ? (
        <div className="w-full h-40 bg-gradient-to-br from-[#7C3AED]/30 to-[#EC4899]/20 flex flex-col items-center justify-center p-3">
          <span className="text-xs text-white font-medium mb-2 truncate w-full text-center">{item.title || item.caption || "Audio"}</span>
          <audio src={item.url} controls className="w-full" />
        </div>
      ) : item.type === "video" ? (
        <video src={item.url} className="w-full h-40 object-contain bg-black pointer-events-none" playsInline />
      ) : (
        <img src={item.url} alt={item.caption || ""} className="w-full h-40 object-cover" />
      )}

      {/* Drag handle — top-left (always visible) */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="absolute top-1.5 left-1.5 w-7 h-7 bg-black/75 hover:bg-[#7C3AED]/90 rounded-full flex items-center justify-center text-white cursor-grab active:cursor-grabbing transition z-10 touch-none shadow-md"
        aria-label="Drag to reorder"
        title="Drag to reorder"
        data-testid={`drag-gallery-${idx}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Position badge */}
      <span className="absolute top-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-black/75 text-white text-[10px] font-semibold z-10 pointer-events-none">
        {idx + 1} / {total}
      </span>

      {/* Remove — always visible */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
        className="absolute top-1.5 right-1.5 w-7 h-7 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition z-10 shadow-md"
        aria-label="Remove from gallery"
        title="Remove"
        data-testid={`remove-gallery-${idx}`}
      >
        <X className="w-4 h-4" />
      </button>

      {/* Mobile-friendly arrow controls — bottom corners */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMove(-1); }}
        disabled={idx === 0}
        className="absolute bottom-1.5 left-1.5 w-7 h-7 bg-black/75 hover:bg-[#7C3AED]/90 disabled:opacity-30 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition z-10 shadow-md"
        aria-label="Move left"
        title="Move left"
        data-testid={`move-gallery-left-${idx}`}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMove(1); }}
        disabled={idx === total - 1}
        className="absolute bottom-1.5 right-1.5 w-7 h-7 bg-black/75 hover:bg-[#7C3AED]/90 disabled:opacity-30 disabled:cursor-not-allowed rounded-full flex items-center justify-center text-white transition z-10 shadow-md"
        aria-label="Move right"
        title="Move right"
        data-testid={`move-gallery-right-${idx}`}
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

